import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Link2, Plus, Trash2, ArrowLeft, Copy, ExternalLink,
  Target, Power, AlertTriangle, Phone, Users, Globe, Flag
} from "lucide-react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import Sidebar from "@/components/Sidebar";
import { copyToClipboard } from "@/lib/clipboard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const BRAND_DOMAIN = "miwap.link";

export default function LinkDetailPage({ user }) {
  const navigate = useNavigate();
  const { linkId } = useParams();
  const [link, setLink] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddDestDialog, setShowAddDestDialog] = useState(false);
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [daysFilter, setDaysFilter] = useState(7);
  const [newDest, setNewDest] = useState({ name: "", dest_type: "whatsapp", url: "", phone: "", message: "", weight: 100 });
  const [bulkText, setBulkText] = useState("");
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    fetchLinkData();
  }, [linkId, daysFilter]);

  const fetchLinkData = async () => {
    try {
      const [linkRes, analyticsRes] = await Promise.all([
        fetch(`${API}/links/${linkId}`, { credentials: "include" }),
        fetch(`${API}/analytics/clicks/${linkId}?days=${daysFilter}`, { credentials: "include" })
      ]);
      if (linkRes.ok) setLink(await linkRes.json());
      else { toast.error("Enlace no encontrado"); navigate("/enlaces"); }
      if (analyticsRes.ok) setAnalytics(await analyticsRes.json());
    } catch (error) {
      toast.error("Error al cargar enlace");
    } finally {
      setLoading(false);
    }
  };

  const handleAddDestination = async (e) => {
    e.preventDefault();
    if (!newDest.name.trim()) { toast.error("Nombre requerido"); return; }
    if (newDest.dest_type === "whatsapp" && !newDest.phone) { 
      toast.error("Teléfono requerido (incluye código de país)"); return; 
    }
    if ((newDest.dest_type === "url" || newDest.dest_type === "whatsapp_group") && !newDest.url) { 
      toast.error("URL requerida"); return; 
    }

    setAdding(true);
    try {
      const response = await fetch(`${API}/links/${linkId}/destinations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(newDest)
      });
      const data = await response.json();
      if (response.ok) {
        setShowAddDestDialog(false);
        setNewDest({ name: "", dest_type: "whatsapp", url: "", phone: "", message: "", weight: 100 });
        toast.success("Destino agregado");
        // Reload page to show new destination
        window.location.reload();
      } else toast.error(data.detail || "Error al agregar");
    } catch (error) {
      toast.error("Error de conexión");
    } finally {
      setAdding(false);
    }
  };

  const handleBulkAdd = async () => {
    const lines = bulkText.trim().split('\n').filter(l => l.trim());
    if (!lines.length) { toast.error("Ingresa al menos un destino"); return; }

    const destinations = lines.map((line, i) => {
      const parts = line.split(',').map(p => p.trim());
      return { name: parts[1] || `Vendedor ${i+1}`, dest_type: "whatsapp", phone: parts[0]?.replace(/\D/g, ''), message: parts[2] || "", weight: 100 };
    });

    setAdding(true);
    try {
      const response = await fetch(`${API}/links/${linkId}/destinations/bulk`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ destinations })
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(`${data.added} destinos agregados`);
        setShowBulkDialog(false);
        setBulkText("");
        fetchLinkData();
      } else toast.error(data.detail || "Error");
    } catch (error) {
      toast.error("Error de conexión");
    } finally {
      setAdding(false);
    }
  };

  const handleToggleDestination = async (destId, isActive) => {
    try {
      await fetch(`${API}/links/${linkId}/destinations/${destId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ is_active: isActive })
      });
      setLink({ ...link, destinations: link.destinations.map(d => d.id === destId ? { ...d, is_active: isActive } : d) });
      toast.success(isActive ? "Activado" : "Desactivado");
    } catch (error) {
      toast.error("Error");
    }
  };

  const handleDeleteDestination = async (destId) => {
    console.log("Delete destination:", destId);
    try {
      const response = await fetch(`${API}/links/${linkId}/destinations/${destId}`, { 
        method: "DELETE", 
        credentials: "include" 
      });
      if (response.ok) {
        toast.success("Destino eliminado");
        window.location.reload();
      } else {
        toast.error("Error al eliminar destino");
      }
    } catch (error) {
      console.error("Delete destination error:", error);
      toast.error("Error al eliminar");
    }
  };

  const handleToggleLinkActive = async () => {
    try {
      await fetch(`${API}/links/${linkId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ is_active: !link.is_active })
      });
      setLink({ ...link, is_active: !link.is_active });
      toast.success(link.is_active ? "Desactivado" : "Activado");
    } catch (error) {
      toast.error("Error");
    }
  };

  const copyLink = async () => {
    const success = await copyToClipboard(`${BRAND_DOMAIN}/${link.slug}`);
    if (success) {
      toast.success("Copiado");
    } else {
      toast.error("No se pudo copiar");
    }
  };

  const getDestIcon = (type) => ({ whatsapp: Phone, whatsapp_group: Users, url: Globe }[type] || Globe);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div></div>;

  const totalWeight = link?.destinations?.reduce((sum, d) => sum + (d.weight || 0), 0) || 0;

  return (
    <div className="min-h-screen bg-background" data-testid="link-detail-page">
      <Sidebar user={user} />

      <main className="md:ml-64 pt-14 md:pt-0 p-4 md:p-8">
        <div className="max-w-5xl mx-auto">
          {/* Back */}
          <Button variant="ghost" className="mb-4 -ml-2" onClick={() => window.location.href = "/enlaces"}>
            <ArrowLeft className="w-4 h-4 mr-2" />Volver
          </Button>

          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-6">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <h1 className="text-xl md:text-3xl font-heading font-black truncate">{link?.name}</h1>
                <div className={`w-3 h-3 rounded-full flex-shrink-0 ${link?.is_active ? "bg-green-500" : "bg-red-500"}`}></div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <code className="text-xs md:text-sm bg-muted px-2 py-1 border break-all">{BRAND_DOMAIN}/{link?.slug}</code>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={copyLink}><Copy className="w-4 h-4" /></Button>
                {link?.destinations?.find(d => d.is_active)?.url && (
                  <a 
                    href={link.destinations.find(d => d.is_active).url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-accent hover:text-accent-foreground"
                    title="Probar enlace"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
              </div>
            </div>
            <Button variant={link?.is_active ? "destructive" : "default"} className="rounded-none w-full md:w-auto" onClick={handleToggleLinkActive}>
              <Power className="w-4 h-4 mr-2" />{link?.is_active ? "Desactivar" : "Activar"}
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <Card className="border-border rounded-none">
              <CardContent className="p-4">
                <p className="label-swiss mb-1 text-[10px] md:text-xs">Clics</p>
                <p className="text-2xl md:text-3xl font-heading font-black text-primary">{link?.total_clicks || 0}</p>
              </CardContent>
            </Card>
            <Card className="border-border rounded-none">
              <CardContent className="p-4">
                <p className="label-swiss mb-1 text-[10px] md:text-xs">Destinos</p>
                <p className="text-2xl md:text-3xl font-heading font-black">{link?.destinations?.length || 0}<span className="text-sm text-muted-foreground">/100</span></p>
              </CardContent>
            </Card>
            <Card className="border-border rounded-none col-span-2">
              <CardContent className="p-4">
                <p className="label-swiss mb-1 text-[10px] md:text-xs">Distribución</p>
                <p className="text-base md:text-lg font-bold">{link?.distribution_type === "weighted" ? "Ponderada" : "Round Robin"}</p>
              </CardContent>
            </Card>
          </div>

          {/* Chart */}
          <Card className="border-border rounded-none mb-6">
            <CardHeader className="flex flex-col md:flex-row md:items-center justify-between gap-2 pb-2">
              <CardTitle className="font-heading text-base">Clics por Día</CardTitle>
              <Select value={String(daysFilter)} onValueChange={(v) => setDaysFilter(Number(v))}>
                <SelectTrigger className="w-full md:w-36 rounded-none"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="7">7 días</SelectItem>
                  <SelectItem value="14">14 días</SelectItem>
                  <SelectItem value="30">30 días</SelectItem>
                </SelectContent>
              </Select>
            </CardHeader>
            <CardContent className="px-2 md:px-6">
              <div className="h-48 md:h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={analytics?.clicks_by_day || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
                    <YAxis tick={{ fontSize: 10 }} width={25} />
                    <Tooltip />
                    <Bar dataKey="clicks" fill="#10B981" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Destinations */}
          <Card className="border-border rounded-none">
            <CardHeader className="flex flex-col md:flex-row md:items-center justify-between gap-3">
              <div>
                <CardTitle className="font-heading text-base">Destinos ({link?.destinations?.length}/100)</CardTitle>
                <p className="text-xs text-muted-foreground mt-1">{link?.distribution_type === "weighted" ? "Ponderado por peso" : "Rotación en orden"}</p>
              </div>
              <div className="flex gap-2 w-full md:w-auto">
                <Button variant="outline" className="rounded-none flex-1 md:flex-none text-xs md:text-sm" onClick={() => setShowBulkDialog(true)}>
                  <Plus className="w-4 h-4 mr-1" />En Masa
                </Button>
                <Button className="rounded-none bg-primary flex-1 md:flex-none text-xs md:text-sm" onClick={() => setShowAddDestDialog(true)} data-testid="add-destination-button">
                  <Plus className="w-4 h-4 mr-1" />Agregar
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {!link?.destinations?.length ? (
                <div className="text-center py-8 border border-dashed">
                  <Target className="w-10 h-10 mx-auto mb-3 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground text-sm">Sin destinos</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {link.destinations.map((dest) => {
                    const Icon = getDestIcon(dest.dest_type);
                    return (
                      <div key={dest.id} className={`p-3 md:p-4 border ${dest.ban_alert ? "border-red-300 bg-red-50" : "border-border"} ${!dest.is_active ? "opacity-50" : ""}`}>
                        <div className="flex items-start gap-3">
                          <Switch checked={dest.is_active} onCheckedChange={(c) => handleToggleDestination(dest.id, c)} className="mt-1" />
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2 mb-1">
                              <Icon className="w-4 h-4 text-muted-foreground" />
                              <span className="font-bold text-sm truncate">{dest.name}</span>
                              {dest.ban_alert && <span className="text-[10px] bg-red-100 text-red-600 px-1"><AlertTriangle className="w-3 h-3 inline" /> BLOQUEADO</span>}
                            </div>
                            <p className="text-xs text-muted-foreground truncate">{dest.phone ? `+${dest.phone}` : dest.url}</p>
                            {link.distribution_type === "weighted" && (
                              <div className="mt-3 max-w-xs">
                                <div className="flex justify-between text-xs mb-1">
                                  <span>Peso</span>
                                  <span>{dest.weight} ({totalWeight ? Math.round((dest.weight/totalWeight)*100) : 0}%)</span>
                                </div>
                                <Slider value={[dest.weight]} min={1} max={100} onValueChange={(v) => setLink({...link, destinations: link.destinations.map(d => d.id === dest.id ? {...d, weight: v[0]} : d)})} />
                              </div>
                            )}
                          </div>
                          <div className="flex flex-col items-end gap-1">
                            <span className="font-bold text-lg">{dest.clicks || 0}</span>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDeleteDestination(dest.id)}><Trash2 className="w-3 h-3" /></Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Add Dialog */}
      <Dialog open={showAddDestDialog} onOpenChange={setShowAddDestDialog}>
        <DialogContent className="rounded-none w-[95vw] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Agregar Destino</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddDestination}>
            <div className="space-y-4 py-4">
              <div>
                <Label>Tipo</Label>
                <Select value={newDest.dest_type} onValueChange={(v) => setNewDest({...newDest, dest_type: v})}>
                  <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">
                    <SelectItem value="whatsapp"><Phone className="w-4 h-4 inline mr-2" />WhatsApp</SelectItem>
                    <SelectItem value="whatsapp_group"><Users className="w-4 h-4 inline mr-2" />Grupo WA</SelectItem>
                    <SelectItem value="url"><Globe className="w-4 h-4 inline mr-2" />URL</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Nombre</Label>
                <Input className="rounded-none" placeholder="Vendedor Juan" value={newDest.name} onChange={(e) => setNewDest({...newDest, name: e.target.value})} data-testid="dest-name-input" />
              </div>
              {newDest.dest_type === "whatsapp" && (
                <>
                  <div>
                    <Label>Teléfono</Label>
                    <Input className="rounded-none" placeholder="521234567890" value={newDest.phone} onChange={(e) => setNewDest({...newDest, phone: e.target.value.replace(/\D/g, "")})} data-testid="dest-phone-input" />
                    <p className="text-xs text-muted-foreground mt-1">Con código de país</p>
                  </div>
                  <div>
                    <Label>Mensaje (opcional)</Label>
                    <Textarea className="rounded-none" placeholder="Hola, me interesa..." value={newDest.message} onChange={(e) => setNewDest({...newDest, message: e.target.value})} />
                  </div>
                </>
              )}
              {(newDest.dest_type === "url" || newDest.dest_type === "whatsapp_group") && (
                <div>
                  <Label>URL</Label>
                  <Input className="rounded-none" placeholder="https://..." value={newDest.url} onChange={(e) => setNewDest({...newDest, url: e.target.value})} data-testid="dest-url-input" />
                </div>
              )}
            </div>
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button type="button" variant="outline" className="rounded-none w-full sm:w-auto" onClick={() => setShowAddDestDialog(false)}>Cancelar</Button>
              <Button type="submit" className="rounded-none w-full sm:w-auto" disabled={adding} data-testid="add-dest-submit">{adding ? "..." : "Agregar"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk Dialog */}
      <Dialog open={showBulkDialog} onOpenChange={setShowBulkDialog}>
        <DialogContent className="rounded-none w-[95vw] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Agregar en Masa</DialogTitle>
            <DialogDescription>Formato: teléfono, nombre, mensaje (uno por línea)</DialogDescription>
          </DialogHeader>
          <Textarea className="rounded-none min-h-[150px] font-mono text-sm" placeholder={`521234567890, Juan, Hola\n529876543210, Maria`} value={bulkText} onChange={(e) => setBulkText(e.target.value)} />
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" className="rounded-none w-full sm:w-auto" onClick={() => setShowBulkDialog(false)}>Cancelar</Button>
            <Button className="rounded-none w-full sm:w-auto" disabled={adding} onClick={handleBulkAdd}>{adding ? "..." : "Agregar Todos"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
